import {
    ACCOUNT_STATUS_FILTER_OPTIONS,
    LOCATION_STATUS_FILTER_OPTIONS,
    REGION_FILTER_OPTIONS,
    type LookupFilterOption,
} from '@/constants/lookupFilterOptions';

/**
 * The fields each record type's create form asks for.
 *
 * The backend publishes no create rules: models.json carries the update rules.
 * So each form lists its fields here, from each type's FormRequest in the
 * paired backend. Only the fields a create needs are asked for, plus a few the
 * person will usually want to set straight away. Everything else is edited on
 * the new record's page.
 *
 * Players are not here because a "new player" is a move, not a create: see
 * AssignNewPlayerDialog. Stations have no form yet, because a station's source
 * needs its own design. See docs/specs/macro-style-followups.md item 17.
 */

export type CreateFieldKind = 'text' | 'email' | 'url' | 'textarea' | 'number' | 'boolean' | 'choice' | 'record';

export interface CreateField {
    /** The request key, for example `location_zone_id`. */
    name: string;
    label: string;
    kind: CreateFieldKind;
    required: boolean;
    /** For `choice`: a fixed list. */
    options?: readonly LookupFilterOption[];
    /** For `choice`: the slug whose records are the options, fetched once. */
    optionsSlug?: string;
    /** For `record`: the slug to search, for a model too big to list whole. */
    recordSlug?: string;
    /** The value the form starts with. */
    initial?: string | number | boolean | null;
    min?: number;
    max?: number;
    /** One line under the field. */
    hint?: string;
    /**
     * Required only for a clients-only user. The backend refuses their create
     * unless this names a zone they can reach.
     */
    requiredForClientsOnly?: boolean;
}

export interface CreateForm {
    entityId: string;
    /** The `_slug` the form posts to. */
    slug: string;
    /** "New account". */
    title: string;
    fields: CreateField[];
    /** Fields the backend requires that the form fills in for you. */
    fillFromSignedInUser?: string[];
}

const text = (name: string, label: string, required = true, extra: Partial<CreateField> = {}): CreateField => ({ name, label, kind: 'text', required, ...extra });
const flag = (name: string, label: string, initial = false): CreateField => ({ name, label, kind: 'boolean', required: true, initial });

export const CREATE_FORMS: Readonly<Record<string, CreateForm>> = {
    Account: {
        entityId: 'Account',
        slug: 'account',
        title: 'New account',
        fields: [
            text('name', 'Name'),
            { name: 'website_url', label: 'Website', kind: 'url', required: true, hint: 'No two accounts can share a website.' },
            { name: 'account_status_id', label: 'Status', kind: 'choice', required: true, options: ACCOUNT_STATUS_FILTER_OPTIONS, initial: 1 },
            { name: 'playlist_manager_group_id', label: 'Playlist manager group', kind: 'choice', required: true, optionsSlug: 'playlist_manager_group' },
            flag('is_onboarding', 'Onboarding', true),
            flag('is_tpg_account', 'TPG account'),
            flag('can_vote', 'Can vote'),
            flag('can_skip', 'Can skip'),
        ],
        // There is no list of staff to pick from yet, so the creator manages the new account.
        fillFromSignedInUser: ['account_manager_user_id'],
    },
    Location: {
        entityId: 'Location',
        slug: 'location',
        title: 'New location',
        fields: [
            text('name', 'Name', true, { hint: 'No two locations can share a name, in any account.' }),
            { name: 'account_id', label: 'Account', kind: 'record', required: true, recordSlug: 'account' },
            { name: 'category_id', label: 'Category', kind: 'choice', required: true, optionsSlug: 'category' },
            { name: 'location_status_id', label: 'Status', kind: 'choice', required: true, options: LOCATION_STATUS_FILTER_OPTIONS, initial: 1 },
            { name: 'region_id', label: 'Region', kind: 'choice', required: true, options: REGION_FILTER_OPTIONS, initial: 1 },
            flag('is_onboarding', 'Onboarding', true),
            flag('already_open', 'Already open'),
        ],
    },
    LocationZone: {
        entityId: 'LocationZone',
        slug: 'location_zone',
        title: 'New zone',
        fields: [
            text('name', 'Name'),
            { name: 'location_id', label: 'Location', kind: 'record', required: true, recordSlug: 'location' },
        ],
    },
    Contact: {
        entityId: 'Contact',
        slug: 'contact',
        title: 'New contact',
        fields: [
            text('first_name', 'First name'),
            text('last_name', 'Last name', false),
            { name: 'email', label: 'Email', kind: 'email', required: true, hint: 'No two contacts can share an email.' },
            { name: 'brand_id', label: 'Brand', kind: 'choice', required: true, optionsSlug: 'brand' },
            {
                name: 'instore_location_zone_id',
                label: 'In-store zone',
                kind: 'record',
                required: false,
                recordSlug: 'location_zone',
                requiredForClientsOnly: true,
                hint: 'The zone this contact works in.',
            },
            flag('is_tpg_contact', 'TPG contact'),
        ],
    },
    Bucket: {
        entityId: 'Bucket',
        slug: 'bucket',
        title: 'New bucket',
        fields: [
            text('name', 'Name'),
            text('short_description', 'Short description'),
            { name: 'location_zone_id', label: 'Zone', kind: 'record', required: true, recordSlug: 'location_zone' },
            { name: 'max_percentage_of_manual_tracks', label: 'Most manual tracks, in percent', kind: 'number', required: true, initial: 0, min: 0, max: 100 },
            { name: 'min_percentage_of_preferred_tracks', label: 'Fewest preferred tracks, in percent', kind: 'number', required: true, initial: 0, min: 0, max: 100 },
            flag('is_base_bucket', 'Base bucket'),
        ],
    },
    LocationZoneCustomPlaylist: {
        entityId: 'LocationZoneCustomPlaylist',
        slug: 'location_zone_custom_playlist',
        title: 'New custom playlist',
        fields: [text('name', 'Name'), { name: 'location_zone_id', label: 'Zone', kind: 'record', required: true, recordSlug: 'location_zone' }],
    },
    ContactLoginCustomPlaylist: {
        entityId: 'ContactLoginCustomPlaylist',
        slug: 'contact_custom_playlist',
        title: 'New contact custom playlist',
        fields: [text('name', 'Name'), { name: 'contact_login_id', label: 'Contact login', kind: 'choice', required: true, optionsSlug: 'contact_login' }],
    },
};

export type CreateValues = Record<string, string | number | boolean | null>;

/** The form as one user sees it: a clients-only user must fill the fields the backend checks against their zones. */
export function formForUser(form: CreateForm, clientsOnly: boolean): CreateForm {
    if (!clientsOnly) return form;
    return { ...form, fields: form.fields.map((field) => (field.requiredForClientsOnly ? { ...field, required: true } : field)) };
}

/** The values a fresh form starts with. */
export function initialValues(form: CreateForm): CreateValues {
    return Object.fromEntries(form.fields.map((field) => [field.name, field.initial ?? (field.kind === 'boolean' ? false : null)]));
}

/** The first required field still empty, by label, or null when the form can be sent. */
export function firstMissingField(form: CreateForm, values: CreateValues): string | null {
    const missing = form.fields.find((field) => {
        if (!field.required || field.kind === 'boolean') return false;
        const value = values[field.name];
        return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
    });
    return missing?.label ?? null;
}

/** The request body: trimmed text, numbers as numbers, empty optional fields left out, and the signed-in user where the form fills it in. */
export function createPayload(form: CreateForm, values: CreateValues, signedInUserId: number | null): Record<string, unknown> {
    const payload: Record<string, unknown> = {};
    for (const field of form.fields) {
        const value = values[field.name];
        if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) continue;
        if (field.kind === 'number' || field.kind === 'choice' || field.kind === 'record') {
            payload[field.name] = Number(value);
        } else if (typeof value === 'string') {
            payload[field.name] = value.trim();
        } else {
            payload[field.name] = value;
        }
    }
    for (const name of form.fillFromSignedInUser ?? []) {
        if (signedInUserId !== null) payload[name] = signedInUserId;
    }
    return payload;
}
